/**
 * Turning stored values into words a person reads without translating.
 *
 * The dashboard used to print the database's own vocabulary straight onto the screen:
 * `0-3_months`, `where_to_live`, `mode_social is dry_run`, `£0.0043`. Every one of those is a
 * small pause for the operator, multiplied by every card and every page. The mapping lives in
 * one file so the words stay the same everywhere.
 */

/** Timeline, in the units the founder actually uses (plan.md section 1.1). */
const TIMELINES: Record<string, string> = {
  imminent: 'moving now',
  '0-3_months': 'in 0–3 months',
  '3-12_months': 'in 3–12 months',
  '12_months_plus': '12+ months away',
  unspecified: 'no date given',
};

const QUESTIONS: Record<string, string> = {
  where_to_live: 'where to live',
  housing: 'housing',
  cost_of_living: 'cost of living',
  schools: 'schools',
  visa: 'visa',
  jobs: 'jobs',
  other: 'other',
};

const PLATFORMS: Record<string, string> = {
  reddit: 'Reddit',
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  discourse: 'Discourse',
  stackexchange: 'Stack Exchange',
  hackernews: 'Hacker News',
  rss: 'RSS',
  email: 'Email',
  unknown: 'unknown',
};

/** `llm_calls.purpose` as words. */
const PURPOSES: Record<string, string> = {
  classify: 'Classify',
  draft: 'Draft replies',
  embed: 'Embed (dedupe)',
  interview: 'Interview',
  summary: 'Summary',
  other: 'Other',
};

/**
 * Draft states, in the operator's language.
 *
 * The database says `sent` and `approved`; the screen says what happened. `sent` is "posted"
 * because a human posts the reply and the row is the record of that - and since 7 Oct 2026 a
 * local poster can post it too, but either way the row means "this went out, and the caps
 * counted it". `approved` was "vetted" from the days when approving did nothing; it now has one
 * writer - the card's Auto post button - so it says what it means: waiting for the local poster.
 */
const STATES: Record<string, string> = {
  pending: 'waiting',
  approved: 'auto post queued',
  sent: 'posted',
  skipped: 'removed',
  rejected: 'rejected',
  failed: 'failed',
};

function lookup(map: Record<string, string>, key: string | null | undefined, fallback: string) {
  if (!key) return fallback;
  return map[key] ?? key.replace(/_/g, ' ');
}

export function timelineLabel(timeline: string | null | undefined): string {
  return lookup(TIMELINES, timeline, 'no date given');
}

export function questionLabel(question: string | null | undefined): string {
  return lookup(QUESTIONS, question, '');
}

export function platformLabel(platform: string | null | undefined): string {
  return lookup(PLATFORMS, platform, 'unknown');
}

export function purposeLabel(purpose: string | null | undefined): string {
  return lookup(PURPOSES, purpose, purpose ?? 'other');
}

export function stateLabel(state: string | null | undefined): string {
  return lookup(STATES, state, state ?? '');
}

/**
 * Money, at the precision a person can use.
 *
 * This is a £0-£20/month budget, so the useful unit below a pound is pence. `£0.0043` is
 * technically exact and tells the reader nothing; `0.43p` is the same number with a scale
 * they already have. Above £1 it is pounds and pence and the exact figure goes in the title.
 */
export function money(gbp: number | null | undefined): string {
  const value = Number(gbp ?? 0);
  if (!Number.isFinite(value) || value === 0) return '£0';
  if (value >= 1) return `£${value.toFixed(2)}`;
  return `${(value * 100).toFixed(2)}p`;
}

/** The unrounded figure, for a tooltip where precision is honest rather than misleading. */
export function moneyExact(gbp: number | null | undefined): string {
  return `£${Number(gbp ?? 0).toFixed(4)}`;
}

/** `2026-09-25` -> `25 Sep`. */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Counts at a glance: `269100` -> `269k`, `6717` -> `6.7k`, `980` -> `980`.
 *
 * The Members and Posts columns exist to be compared across fifty rows, not read exactly, and a
 * full `335,037` in a narrow column is a number the eye has to stop for. The precise figure goes
 * in the cell's `title`, so nothing is lost - only the reading of it is deferred.
 */
export function compactCount(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value < 1_000) return String(value);
  if (value < 10_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  if (value < 1_000_000) return `${Math.round(value / 1_000)}k`;
  return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
}

/** `25 Sep, 14:02`. */
export function shortDateTime(value: string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Where a watched community lives, so its row can open it.
 *
 * The Facebook identifier is `fb/group/<slug>` and the URL is that slug under
 * `facebook.com/groups` - the same derivation `subjectsFor()` makes when it builds a run's start
 * URLs, which is what keeps this link and the next scan pointed at the same place. Other
 * platforms' identifiers do not carry a URL, and a guessed one would 404 in the operator's
 * browser, so they get no link rather than a wrong one.
 */
export function groupUrl(platform: string, identifier: string): string | null {
  if (platform !== 'facebook') return null;
  const slug = /^fb\/group\/(.+)$/i.exec(identifier)?.[1];
  return slug ? `https://www.facebook.com/groups/${slug}` : null;
}
